import React, {
  useCallback, useEffect, useReducer, useState,
} from 'react';
import { Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataReducerActions as dataReducerActions, DataReducerActions } from 'shared/reducers';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { DependentAsyncSearchableSelect } from 'shared/components/select/SearchableSelects';
import { withAsyncSelect, withStaticSelect } from 'shared/components/data-display/withSelectors';
import { DPSK_TYPES, DPSK_MIN_LENGTH } from 'shared/constants/building';
import { vszEntity } from 'shared/entityDetails';
import { NumberEditor } from 'shared/components/data-display/editors';
import { VARIANTS } from 'shared/constants';
import { useNotification, useRequest } from 'shared/hooks';
import { configureGroupDpsks, getSmartzone, setSmartzone } from 'shared/api/wireless';
import { getSmartzoneWlans, getVszZones } from 'shared/api/vsz';
import { updateBuildingDot1x } from 'shared/api/buildings';
import ZoneEditDialog from 'features/networking/complex/ZoneEditDialog';

const initialSmartzoneData = {
  dpsk_type: DPSK_TYPES.group,
  dpsk_length: DPSK_MIN_LENGTH,
};

function reducer(state, {
  action,
  payload,
}) {
  const newState = state;
  switch (action) {
    case dataReducerActions.set:
      if (payload.key === 'id') {
        newState.vsz = {};
        newState.vsz[payload.key] = payload.value;
        return {
          ...newState,
          zone: undefined,
          wlan: undefined,
        };
      }
      if (payload.key === 'zone') {
        newState[payload.key] = payload.value;
        return {
          ...newState,
          wlan: undefined,
        };
      }
      newState[payload.key] = payload.value;
      return { ...newState };
    case dataReducerActions.clear:
      return initialSmartzoneData;
    default:
      return state;
  }
}

const dpskTypeDescription = [
  {
    key: 'dpsk_type',
    label: 'DPSK Type',
    editor: withStaticSelect({
      disableClearable: true,
      options: [DPSK_TYPES.group],
      valueAsLabel: true,
    }),
  }];

const vszDescription = [{
  key: 'id',
  label: 'VSZ',
  editor: withAsyncSelect({
    ...vszEntity,
    preloadOptions: true,
  }),
},
];

const dpskLengthDescription = [
  {
    key: 'dpsk_length',
    label: 'DPSK Length',
    editor: NumberEditor,
  },
];

/**
 * @param {Object} props
 * @param {Object} props.complex - Complex object
 * @param {Function} props.triggerUpdate - Function to trigger update
 * @param {number} props.updateCount - Update counter
 */
export default function ZonesPanel({
  complex,
  triggerUpdate,
  updateCount,
}) {
  const { request } = useRequest();
  const notification = useNotification();
  const [state, dispatch] = useReducer(reducer, initialSmartzoneData);

  const {
    buildings,
    vsz,
    wlan,
    zone,
  } = state;

  const {
    id: vszId,
  } = vsz || {};

  const update = (key, value) => dispatch({
    action: DataReducerActions.set,
    payload: {
      key,
      value,
    },
  });

  useEffect(() => {
    const newBuildings = [];
    (async () => {
      await Promise.all(complex.buildings
        .map((building) => building?.dot1x?.smartzone_id
          && request(getSmartzone(building?.dot1x?.smartzone_id))))
        .then((results) => results
          .map((result, i) => (
            newBuildings
              .push(result ? {
                ...complex.buildings[i],
                dot1x: {
                  ...complex.buildings[i].dot1x,
                  smartzone: { ...result },
                },
              } : complex.buildings[i])
          )));
    })();
    update('buildings', newBuildings);
  }, [complex.buildings, request]);

  useEffect(() => {
    (async () => {
      if (vszId) {
        const response = await request(vszEntity.getRequest(vszId));
        if (response) {
          update('vsz', response);
        }
      }
    })();
    // Updating deps per eslint recommendation triggers infinite request loop.
  }, [request, vszId, updateCount]);

  const [validUpdates, setValidUpdates] = useState(true);

  const onWlanChange = (value) => {
    update('wlan', value);
  };

  const onZoneChange = (value) => {
    update('zone', value);
  };

  useEffect(() => {
    if (state?.vsz?.id && state?.zone?.id && state?.wlan?.id) {
      setValidUpdates(true);
      return;
    }
    setValidUpdates(false);
  }, [state]);

  const setComplexGroupDPSK = async () => {
    const reqs = [];
    buildings.forEach((building) => {
      if (building.dot1x?.smartzone_id) {
        reqs.push({
          b: building.name,
          r: configureGroupDpsks(building.id),
        });
      }
    });

    const rowResults = Promise.all(reqs.map(({
      b,
      r,
    }) => request(r, `Updated ${b} DPSKs`)));
    if (rowResults) {
      notification.pushSuccess('Updated Complex DPSKs');
    }
  };

  const setBuildingZones = async (updateBuildings) => {
    // Create or retrieve a smartzoneRecord based on provided data
    await request(setSmartzone({
      dpsk_length: state.dpsk_length,
      wlan_ssid: wlan.ssid,
      zone_name: zone.name,
      vsz_id: vsz.id,
      dpsk_type: state.dpsk_type,
    }))
      .then((result) => {
        const reqs = [];
        updateBuildings.forEach((building) => {
          if (building.dot1x !== null && building.dot1x.building_id) {
            reqs.push(updateBuildingDot1x(building.id, {
              smartzone_id: result.id,
            }));
          }
        });

        const rowResults = Promise.all(reqs.map((r) => request(r, 'Updated default')));
        if (rowResults) {
          notification.pushSuccess('Updated Building Smartzone');
          triggerUpdate();
          dispatch({ action: DataReducerActions.clear });
        }
        return null;
      });
  };

  // Memoized: a new searchRequest identity re-fires the select's load effect in a loop.
  const getVszWlan = useCallback(
    (dependentZoneId) => getSmartzoneWlans(vszId, dependentZoneId),
    [vszId],
  );

  return (
    <Grid
      container
      columnSpacing={2}
      sx={{
        justifyContent: 'flex-end',
      }}
    >
      <Grid size={12}>
        <DataFormatter
          data={initialSmartzoneData}
          descriptions={dpskTypeDescription}
          editing
          updateValue={update}
          updatedData={state}
        />
      </Grid>
      <Grid size={12}>
        <DataFormatter
          data={initialSmartzoneData}
          descriptions={vszDescription}
          editing
          updateValue={update}
          updatedData={vsz}
        />
      </Grid>
      <Grid size={{
        xs: 12,
        md: 6,
      }}
      >
        <DependentAsyncSearchableSelect
          label="Zone Name"
          getOptionLabel={(o) => o.name}
          onChange={onZoneChange}
          dependentId={vszId}
          preloadOptions
          searchRequest={getVszZones}
          selectedOption={zone}
          placeholder="Select Zone..."
        />
      </Grid>
      <Grid size={{
        xs: 12,
        md: 6,
      }}
      >
        <DependentAsyncSearchableSelect
          label="WLAN"
          getOptionLabel={(o) => o.ssid}
          onChange={onWlanChange}
          disabled={!zone?.id}
          dependentId={zone?.id}
          preloadOptions
          searchRequest={getVszWlan}
          selectedOption={wlan}
          placeholder="Select WLAN..."
        />
      </Grid>
      <Grid size={12}>
        <DataFormatter
          data={initialSmartzoneData}
          descriptions={dpskLengthDescription}
          editing
          updateValue={update}
          updatedData={state}
        />
      </Grid>
      <Grid
        container
        size={12}
        spacing={2}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Grid>
          <ZoneEditDialog
            complex={{
              ...complex,
              buildings,
            }}
            state={state}
            setDefault={(rows) => setBuildingZones(rows)}
          />
        </Grid>
        <Grid>
          <Button
            disabled={!validUpdates}
            onClick={() => setBuildingZones(buildings)}
            variant={VARIANTS.contained}
          >
            Set Default
          </Button>
        </Grid>
        <Grid>
          <Button variant={VARIANTS.contained} onClick={setComplexGroupDPSK}>
            Set Group DPSKs
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
}
